/**
 * /admin/permissions — the ABAC matrix in the language of the work, not the
 * RPC layer. Rows are the things a person can SEE (derived from the view-mode
 * ladder + the sensitive-field registry in enies-lobby main.go) and the things
 * a person can DO (each catalog verb translated per its real enforcement
 * site — e.g. `commit` gates applying a contract mod in zou, `manage_quality`
 * gates the audit lifecycle in ryugu). Columns are the role bundles.
 *
 * Data: AdminListBundles (permitted_actions + requires_break_glass_for).
 * Unknown verbs still render (raw, under "Other") so a new action can never
 * silently vanish from this page.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check, Eye, EyeOff, Lock, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useDeleteBundle, useListBundles, useListDataPacks } from '../Access/hooks';
import { actionLabel, CAPABILITY_GROUPS, canSee, SEE_ROWS, unknownActions, VIEW_MODE_LEGEND, viewModeClass, viewModeLabel } from './vocabulary';
// Re-exports: grant dialogs, groups page, and tests import these from here.
export { actionLabel, CAPABILITY_GROUPS, canSee, unknownActions, VIEW_MODE_LEGEND, ViewModeBadge, viewModeClass, viewModeLabel } from './vocabulary';
import { RoleEditorDrawer } from './RoleEditorDrawer';
import { RoleNamesPanel } from './RoleNamesPanel';
import { TemplatesGallery } from './TemplatesGallery';
function DoCell({ bundle, action }) {
    if (bundle.requiresBreakGlassFor.includes(action)) {
        return /*#__PURE__*/ _jsx(Lock, {
            className: "mx-auto h-3.5 w-3.5 text-amber-600",
            "aria-label": `${actionLabel(action)} requires break-glass`
        });
    }
    if (bundle.permittedActions.includes(action)) {
        return /*#__PURE__*/ _jsx(Check, {
            className: "mx-auto h-4 w-4 text-emerald-600",
            "aria-label": actionLabel(action)
        });
    }
    return /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground/40",
        children: "—"
    });
}
export function PermissionsPage() {
    const { data: bundles, isLoading } = useListBundles(true);
    const { data: dataPacks } = useListDataPacks();
    // K5: bundles carry pack KEYS; the matrix needs field names for row math.
    // Expand once against the server-owned catalog.
    const packFieldsByKey = useMemo(()=>new Map((dataPacks ?? []).map((p)=>[
                p.key,
                p.fields
            ])), [
        dataPacks
    ]);
    const unmaskedFieldsFor = (packKeys)=>{
        const out = new Set();
        for (const k of packKeys ?? [])for (const f of packFieldsByKey.get(k) ?? [])out.add(f);
        return [
            ...out
        ];
    };
    const [filter, setFilter] = useState('');
    const [editorOpen, setEditorOpen] = useState(false);
    const [editor, setEditor] = useState({
        mode: 'create'
    });
    const [menuFor, setMenuFor] = useState(null);
    const deleteBundle = useDeleteBundle();
    const openEditor = (state)=>{
        setEditor(state);
        setEditorOpen(true);
        setMenuFor(null);
    };
    const visible = useMemo(()=>{
        const all = bundles ?? [];
        const f = filter.trim().toLowerCase();
        if (!f) return all;
        return all.filter((b)=>b.key.toLowerCase().includes(f) || b.displayName.toLowerCase().includes(f));
    }, [
        bundles,
        filter
    ]);
    const other = useMemo(()=>unknownActions(bundles ?? []), [
        bundles
    ]);
    const groupHeader = (title)=>/*#__PURE__*/ _jsx("tr", {
            className: "border-t bg-muted/30",
            children: /*#__PURE__*/ _jsx("td", {
                colSpan: visible.length + 2,
                className: "sticky left-0 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                children: title
            })
        });
    const rowLabel = (label, description)=>/*#__PURE__*/ _jsxs("td", {
            className: "sticky left-0 z-10 max-w-[18rem] bg-background px-3 py-2",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "font-medium",
                    children: label
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "text-xs text-muted-foreground",
                    children: description
                })
            ]
        });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        "data-testid": Testids.adminConsole.permissionsPage,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end justify-between gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-lg font-semibold",
                                children: "What each role can see and do"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "max-w-2xl text-sm text-muted-foreground",
                                children: "A user or group holding a role on a scope gets exactly this for everything under that scope. Read down a column to understand a role; read across a row to see who can do a thing."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Input, {
                                value: filter,
                                onChange: (e)=>setFilter(e.target.value),
                                placeholder: "Filter roles…",
                                className: "h-9 w-64",
                                "data-testid": Testids.adminConsole.permissionsFilter
                            }),
                            /*#__PURE__*/ _jsxs(Button, {
                                "data-testid": Testids.adminConsole.permissionsCreateButton,
                                onClick: ()=>openEditor({
                                        mode: 'create'
                                    }),
                                children: [
                                    /*#__PURE__*/ _jsx(Plus, {
                                        className: "mr-1.5 h-4 w-4"
                                    }),
                                    "Create custom role"
                                ]
                            })
                        ]
                    })
                ]
            }),
            isLoading ? /*#__PURE__*/ _jsx("p", {
                className: "text-muted-foreground",
                children: "Loading catalog…"
            }) : /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto rounded-md border",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full text-sm",
                    "data-testid": Testids.adminConsole.permissionsMatrix,
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            className: "bg-muted/40",
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "sticky left-0 z-10 bg-muted/40 px-3 py-2 text-left align-bottom font-medium",
                                        children: "Role"
                                    }),
                                    visible.map((b)=>/*#__PURE__*/ _jsxs("th", {
                                            className: "group relative h-44 min-w-[2.75rem]",
                                            "data-testid": Testids.adminConsole.permissionsRow,
                                            "data-bundle-key": b.key,
                                            children: [
                                                /*#__PURE__*/ _jsx("button", {
                                                    type: "button",
                                                    "data-testid": Testids.adminConsole.roleColumnMenu,
                                                    "data-bundle-key": b.key,
                                                    title: `${b.displayName || b.key}${b.isGlobal ? ' (built-in)' : ''} — ${b.description || ''}`,
                                                    onClick: ()=>setMenuFor(menuFor === b.key ? null : b.key),
                                                    className: "absolute bottom-8 left-1/2 origin-bottom-left -rotate-45 whitespace-nowrap text-left text-xs font-medium hover:text-primary",
                                                    children: b.displayName || b.key
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: `absolute bottom-3 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full ${viewModeClass(b.defaultViewMode)}`,
                                                    title: `Visibility: ${viewModeLabel(b.defaultViewMode)}`
                                                }),
                                                menuFor === b.key && /*#__PURE__*/ _jsxs("div", {
                                                    className: "absolute left-1/2 top-full z-20 mt-1 w-36 -translate-x-1/2 rounded-md border bg-popover p-1 text-left shadow-md",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: "px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground",
                                                            children: b.isGlobal ? 'Built-in role' : b.sourceTemplateFamilyId ? `From template v${b.sourceTemplateVersion || 1}` : 'Custom role'
                                                        }),
                                                        !b.isGlobal && /*#__PURE__*/ _jsx("button", {
                                                            type: "button",
                                                            "data-testid": Testids.adminConsole.roleColumnEdit,
                                                            className: "block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-accent",
                                                            onClick: ()=>openEditor({
                                                                    mode: 'edit',
                                                                    initial: b
                                                                }),
                                                            children: "Edit"
                                                        }),
                                                        /*#__PURE__*/ _jsx("button", {
                                                            type: "button",
                                                            "data-testid": Testids.adminConsole.roleColumnDuplicate,
                                                            className: "block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-accent",
                                                            onClick: ()=>openEditor({
                                                                    mode: 'create',
                                                                    initial: {
                                                                        ...b,
                                                                        key: `${b.key}_copy`,
                                                                        displayName: `${b.displayName} (copy)`
                                                                    }
                                                                }),
                                                            children: "Duplicate"
                                                        }),
                                                        !b.isGlobal && /*#__PURE__*/ _jsx("button", {
                                                            type: "button",
                                                            "data-testid": Testids.adminConsole.roleColumnDelete,
                                                            className: "block w-full rounded px-2 py-1.5 text-left text-xs text-destructive hover:bg-accent",
                                                            onClick: ()=>{
                                                                if (window.confirm(`Delete role "${b.displayName}"? Grants must be revoked first.`)) deleteBundle.mutate(b.key);
                                                                setMenuFor(null);
                                                            },
                                                            children: "Delete"
                                                        })
                                                    ]
                                                })
                                            ]
                                        }, b.key)),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "w-28"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("tbody", {
                            children: [
                                groupHeader('What they can see'),
                                SEE_ROWS.map((row)=>/*#__PURE__*/ _jsxs("tr", {
                                        className: "border-t hover:bg-muted/20",
                                        children: [
                                            rowLabel(row.label, row.description),
                                            visible.map((b)=>{
                                                const state = canSee(b.defaultViewMode, row.key, unmaskedFieldsFor(b.dataPacks));
                                                return /*#__PURE__*/ _jsx("td", {
                                                    className: "px-1 py-2 text-center",
                                                    children: state === 'full' ? /*#__PURE__*/ _jsx(Eye, {
                                                        className: "mx-auto h-4 w-4 text-emerald-600",
                                                        "aria-label": `sees ${row.label}`
                                                    }) : state === 'partial' ? /*#__PURE__*/ _jsx(Eye, {
                                                        className: "mx-auto h-4 w-4 text-amber-600",
                                                        "aria-label": `sees part of ${row.label}`
                                                    }) : /*#__PURE__*/ _jsx(EyeOff, {
                                                        className: "mx-auto h-3.5 w-3.5 text-muted-foreground/50",
                                                        "aria-label": `${row.label} hidden`
                                                    })
                                                }, b.key);
                                            })
                                        ]
                                    }, row.key)),
                                CAPABILITY_GROUPS.map((g)=>/*#__PURE__*/ _jsx(FragmentRows, {
                                        header: groupHeader(g.group),
                                        rows: g.items.map((cap)=>/*#__PURE__*/ _jsxs("tr", {
                                                className: "border-t hover:bg-muted/20",
                                                children: [
                                                    rowLabel(cap.label, cap.description),
                                                    visible.map((b)=>/*#__PURE__*/ _jsx("td", {
                                                            className: "px-1 py-2 text-center",
                                                            children: /*#__PURE__*/ _jsx(DoCell, {
                                                                bundle: b,
                                                                action: cap.action
                                                            })
                                                        }, b.key))
                                                ]
                                            }, cap.action))
                                    }, g.group)),
                                other.length > 0 && /*#__PURE__*/ _jsx(FragmentRows, {
                                    header: groupHeader('Other actions'),
                                    rows: other.map((action)=>/*#__PURE__*/ _jsxs("tr", {
                                            className: "border-t hover:bg-muted/20",
                                            children: [
                                                rowLabel(action, 'No description yet — raw action name'),
                                                visible.map((b)=>/*#__PURE__*/ _jsx("td", {
                                                        className: "px-1 py-2 text-center",
                                                        children: /*#__PURE__*/ _jsx(DoCell, {
                                                            bundle: b,
                                                            action: action
                                                        })
                                                    }, b.key))
                                            ]
                                        }, action))
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(RoleNamesPanel, {}),
            /*#__PURE__*/ _jsx(TemplatesGallery, {
                bundles: bundles ?? []
            }),
            /*#__PURE__*/ _jsxs(Card, {
                children: [
                    /*#__PURE__*/ _jsx(CardHeader, {
                        className: "pb-2",
                        children: /*#__PURE__*/ _jsx(CardTitle, {
                            className: "text-sm",
                            children: "How visibility works"
                        })
                    }),
                    /*#__PURE__*/ _jsx(CardContent, {
                        children: /*#__PURE__*/ _jsxs("dl", {
                            className: "grid gap-2 sm:grid-cols-2 lg:grid-cols-3",
                            children: [
                                VIEW_MODE_LEGEND.map((l)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-start gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(Badge, {
                                                variant: "secondary",
                                                className: `mt-0.5 shrink-0 ${l.className}`,
                                                children: l.label
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs text-muted-foreground",
                                                children: l.meaning
                                            })
                                        ]
                                    }, l.mode)),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-start gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(Lock, {
                                            className: "mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-xs text-muted-foreground",
                                            children: "Break-glass: usable only during an approved, time-boxed emergency grant. Every use is audited."
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(RoleEditorDrawer, {
                open: editorOpen,
                onOpenChange: setEditorOpen,
                editor: editor
            })
        ]
    });
}
// Renders a group-header row followed by its capability rows. Tables can't
// nest fragments with keys inline cleanly inside map(), so this tiny helper
// keeps the tbody flat.
function FragmentRows({ header, rows }) {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            header,
            rows
        ]
    });
}
